import type { QueryStringInput } from '@hello-aria/query-string'
import type {
    CreateReceivable,
    CreateReceivableGateway,
    Receivable,
    ReceivableGateway,
    UpdateReceivable,
    UpdateReceivableGateway
} from '@/shared/models/receivableModels'
import { batchFetch } from '@/shared/utils/requestsUtils'
import { type Page, toListQuery } from '@hello-aria/makalu-api-client'
import { makaluApi } from '@/shared/utils/makaluApiClient'
import { isEmpty, isNil } from 'lodash'
import { downloadAttachments, uploadFiles } from './uploadServices'
import { createProofs } from './proofsServices'
import type { UploadFileGateway } from '@/shared/models/uploadModels'
import { requireFileId } from '@/shared/models/uploadModels'
import { receivableInvoiceIsDueSoon, receivableInvoiceIsOverdue } from '@/shared/utils/receivableUtils'
import { trackEvent } from '@/shared/utils/analyticsUtils'

interface GetReceivablesParams {
    companyId: string
    query?: QueryStringInput
    hasAttachments?: boolean
    signal?: AbortSignal
}

export const listReceivables = batchFetch(async (params: GetReceivablesParams): Promise<Page<Receivable>> => {
    const page = await makaluApi.listPage('/companies/{companyId}/receivables', {
        params: {
            path: { companyId: params.companyId },
            query: toListQuery({
                ...params.query,
                sorts: params.query?.sorts ?? [
                    {
                        path: 'createdAt',
                        direction: 'desc'
                    }
                ]
            })
        },
        signal: params.signal
    })

    const mappedReceivables = await Promise.all(page.body.map((r) => resolveReceivable(r, false)))
    const sanitizedMappedReceivables = mappedReceivables.filter((r): r is Receivable => !isNil(r))

    return {
        ...page,
        body: sanitizedMappedReceivables
    }
})

export const resolveReceivable = async (
    data: ReceivableGateway | undefined,
    hasAttachments = true
): Promise<Receivable | undefined> => {
    if (isNil(data)) return undefined

    const invoiceIsDueSoon = receivableInvoiceIsDueSoon(data)
    const invoiceIsOverdue = receivableInvoiceIsOverdue(data)

    if (isNil(data.attachments) || isEmpty(data.attachments) || !hasAttachments) {
        return { ...data, attachments: undefined, invoiceIsDueSoon, invoiceIsOverdue }
    }

    const attachments = await downloadAttachments(data.attachments)
    const attachmentCount = attachments?.length ?? 0
    return {
        ...data,
        attachments,
        attachmentCount,
        invoiceIsDueSoon,
        invoiceIsOverdue
    }
}

export const getReceivable = async (
    companyId: string,
    receivableId: string | null | undefined
): Promise<Receivable | undefined> => {
    if (!receivableId) {
        return undefined
    }

    const receivable = await makaluApi.GET('/companies/{companyId}/receivables/{receivableId}', {
        params: {
            path: { companyId, receivableId },
            query: toListQuery({
                params: {
                    include: 'attachmentUrl'
                }
            })
        }
    })

    return resolveReceivable(receivable)
}

const uploadAttachmentIds = async (attachments: File[], companyId: string) => {
    const files = await uploadFiles(attachments, companyId)
    const sanitizedFiles = files?.filter((file): file is UploadFileGateway => !isNil(file))
    return sanitizedFiles?.map((file) => ({ id: requireFileId(file) }))
}

export const createReceivable = async (
    companyId: string,
    payload: CreateReceivable
): Promise<Receivable | undefined> => {
    const { proofs, attachments, ...rest } = payload

    // The API rejects attachments alongside proof ids, so a receivable with proofs sends only their ids.
    const formattedPayload: CreateReceivableGateway = isNil(proofs)
        ? { ...rest, attachments: await uploadAttachmentIds(attachments, companyId) }
        : { ...rest, proofIds: await createProofs({ companyId, drafts: proofs }) }

    const receivable = await makaluApi.POST('/companies/{companyId}/receivables', {
        params: { path: { companyId } },
        body: formattedPayload
    })

    trackEvent('Create_Receivable')

    return resolveReceivable(receivable)
}

export const updateReceivable = async (
    companyId: string,
    payload: UpdateReceivable
): Promise<Receivable | undefined> => {
    const { receivableId, ...rest } = payload

    const files = await uploadFiles(payload.attachments, companyId)
    const sanitizedFiles = files?.filter((file): file is UploadFileGateway => !isNil(file))
    const mappedFiles = sanitizedFiles?.map((file) => ({ id: requireFileId(file) }))

    const formattedPayload: UpdateReceivableGateway = {
        ...rest,
        // api/receivable/schemas/EditReceivableBody.ts refuses a null owner, so an unset owner stays out of the body.
        receivableOwner: rest.receivableOwner ?? undefined,
        attachments: mappedFiles
    }

    const receivable = await makaluApi.PATCH('/companies/{companyId}/receivables/{receivableId}', {
        params: { path: { companyId, receivableId } },
        body: formattedPayload
    })

    trackEvent('Update_Receivable')

    return resolveReceivable(receivable)
}
